import React from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SwipeBack from 'src/react/_Layout/SwipeBack';
import {
  AppBarTitle,
  CloseLabel,
  DialogContent,
  FloatingCloseButton,
  SecondaryActionArea,
  SecondaryActionLabel,
  StyledAppBar,
} from 'src/react/_Layout/ModalDialog.styles';
import { useHistory } from 'react-router';
import { TransitionDown, TransitionLeft, TransitionRight, TransitionUp } from '@sekoia/shared/components/Transitions';
import { useIsMobile } from 'src/hooks/ui/useIsMobile';

type ModalDialogProps = {
  open: boolean;
  formDialog?: boolean;
  title?: string | null | React.ReactElement;
  dialogHeight?: string;
  appBarType?: 'regular' | 'closeAndBackButton' | 'closeButton' | 'none';
  closeAsLabel?: boolean;
  handler?: () => void;
  customGoBack?: () => void;
  actionButtons?: React.ReactElement;
  transition?: 'up' | 'down' | 'left' | 'right';
  zIndexSet?: number;
  backgroundColor?: string;
};

export const ModalDialog = ({
  title,
  handler,
  customGoBack,
  appBarType = 'regular',
  closeAsLabel = false,
  formDialog = true,
  open,
  actionButtons,
  dialogHeight,
  transition,
  zIndexSet,
  backgroundColor,
  children,
}: React.PropsWithChildren<ModalDialogProps>) => {
  const history = useHistory();
  const { t } = useTranslation(['global']);
  const isMobile = useIsMobile();

  const handleClose = () => {
    history.goBack();
  };

  const ActionWrapper = closeAsLabel ? SecondaryActionLabel : SecondaryActionArea;

  const renderAppBar = () => {
    switch (appBarType) {
      case 'regular':
        return (
          <StyledAppBar elevation={0}>
            <Toolbar variant="dense" disableGutters>
              <IconButton color="inherit" onClick={handler || handleClose} aria-label={t('actions.close')} size="large">
                <CloseIcon />
              </IconButton>
              <AppBarTitle variant="subtitle1" color="inherit">
                {title}
              </AppBarTitle>
              {actionButtons}
            </Toolbar>
          </StyledAppBar>
        );
      case 'closeAndBackButton':
        return (
          <StyledAppBar elevation={0}>
            <Toolbar variant="dense" disableGutters>
              <IconButton
                color="inherit"
                onClick={customGoBack || handleClose}
                aria-label={t('actions.back')}
                size="large"
              >
                <ArrowBack />
              </IconButton>
              <AppBarTitle variant="subtitle1" color="inherit">
                {title}
              </AppBarTitle>
              <ActionWrapper>
                {!closeAsLabel && (
                  <IconButton
                    color="inherit"
                    onClick={handler || handleClose}
                    aria-label={t('actions.close')}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
                {closeAsLabel && (
                  <CloseLabel variant="subtitle1" color="inherit" onClick={handler || handleClose}>
                    {t('actions.close')}
                  </CloseLabel>
                )}
              </ActionWrapper>
            </Toolbar>
          </StyledAppBar>
        );
      case 'closeButton':
        return (
          <FloatingCloseButton
            size="medium"
            color="inherit"
            onClick={handler || handleClose}
            aria-label={t('actions.close')}
          >
            <CloseIcon />
          </FloatingCloseButton>
        );
      case 'none':
        return null;
      default:
        return null;
    }
  };

  const renderMarginBottomBox = () => {
    // Spacer keeps form fields above the on-screen keyboard; on desktop it only causes a double scrollbar
    if (!formDialog || !isMobile) return null;
    const height = window.innerHeight;
    return <Box sx={{ height }}>&nbsp;</Box>;
  };

  const renderTransition = () => {
    if (!transition) return undefined;
    switch (transition) {
      case 'up':
        return TransitionUp;
      case 'down':
        return TransitionDown;
      case 'left':
        return TransitionLeft;
      case 'right':
        return TransitionRight;

      default:
        return undefined;
    }
  };

  return (
    <Dialog
      disableEscapeKeyDown={true}
      fullScreen={true}
      open={open}
      slots={{ transition: renderTransition() }}
      sx={{ zIndex: zIndexSet }}
    >
      {renderAppBar()}
      <DialogContent backgroundColor={backgroundColor}>
        <Box sx={{ maxWidth: formDialog ? 600 : 'unset', height: dialogHeight ?? 'unset' }}>
          {children}
          {renderMarginBottomBox()}
        </Box>
      </DialogContent>
      {appBarType === 'closeAndBackButton' && <SwipeBack handler={customGoBack || handleClose} />}
    </Dialog>
  );
};
